import dayjs from 'dayjs';
import {
	MemberStatusEnum,
	RiskLevelEnum,
	ServiceFollowEnum,
} from '@/api/enum/common.enum';

/**
 * How long a case may go without a meeting intervention before the
 * "Last meeting intervention date" column flags it, by current risk level.
 */
export const LAST_MEETING_ALERT_DAYS: Record<number, number> = {
	[RiskLevelEnum.High]: 7,
	[RiskLevelEnum.Medium]: 14,
	[RiskLevelEnum.Low]: 42,
};

interface LastMeetingRow {
	lastMeetingInterventionDate?: string | Date;
	currentRiskLevel?: number;
	serviceFollow?: number;
	status?: number;
}

export interface LastMeetingAlert {
	daysSinceLastMeeting: number;
	alertAfterDays: number;
}

/**
 * The overdue meeting intervention behind the alert icon, or undefined when the
 * case is not flagged. The two figures are what the icon's tooltip reports, so
 * staff can see why the row was picked out.
 *
 * Activity (program) members are followed by activities rather than meeting
 * interventions, so they are never flagged, and neither are cases that are no
 * longer counted. A case with no meeting yet, or no risk level, has nothing to
 * measure against and is left unflagged.
 */
export function getLastMeetingAlert(
	row: LastMeetingRow
): LastMeetingAlert | undefined {
	if (row.serviceFollow === ServiceFollowEnum.Program) return undefined;
	if (row.status === MemberStatusEnum.NotCounted) return undefined;

	const alertAfterDays =
		row.currentRiskLevel == null
			? undefined
			: LAST_MEETING_ALERT_DAYS[row.currentRiskLevel];
	if (alertAfterDays === undefined) return undefined;

	const lastMeeting = dayjs(row.lastMeetingInterventionDate);
	if (!row.lastMeetingInterventionDate || !lastMeeting.isValid())
		return undefined;

	const daysSinceLastMeeting = dayjs()
		.startOf('day')
		.diff(lastMeeting.startOf('day'), 'day');

	return daysSinceLastMeeting > alertAfterDays
		? { daysSinceLastMeeting, alertAfterDays }
		: undefined;
}

/** Days a centre has to complete an intake, counted from the referral date. */
export const INTAKE_COMPLETION_DAYS = 56;
/** How close to the due date an unfinished intake starts being prompted. */
export const INTAKE_DUE_SOON_DAYS = 14;

export type IntakeDueIcon = 'none' | 'completed' | 'dueSoon' | 'overdue';

interface IntakeDueRow {
	receivingReferralOrApplicationDate?: string | Date;
	dueDateOfIntakeCompletion?: string | Date;
	status?: number;
}

/**
 * Which icon belongs next to the due date of intake completion: a tick once the
 * intake is done, a warning once the due date has passed, and a prompt over the
 * last two weeks before it. Cases that are no longer counted are never marked.
 *
 * The due date returned by the API is what the column shows, so it is what the
 * icons are measured against; referral + 56 days only stands in when the API
 * has not filled it in.
 */
export function getIntakeDueIcon(row: IntakeDueRow): IntakeDueIcon {
	if (row.status === MemberStatusEnum.NotCounted) return 'none';
	if (
		row.status === MemberStatusEnum.IntakeCompletedNoAdmitted ||
		row.status === MemberStatusEnum.IntakeCompletedAdmitted
	)
		return 'completed';

	const apiDueDate = dayjs(row.dueDateOfIntakeCompletion);
	const referralDate = dayjs(row.receivingReferralOrApplicationDate);
	const dueDate =
		row.dueDateOfIntakeCompletion && apiDueDate.isValid()
			? apiDueDate
			: row.receivingReferralOrApplicationDate && referralDate.isValid()
				? referralDate.add(INTAKE_COMPLETION_DAYS, 'day')
				: undefined;
	if (!dueDate) return 'none';

	const daysUntilDue = dueDate
		.startOf('day')
		.diff(dayjs().startOf('day'), 'day');
	if (daysUntilDue < 0) return 'overdue';
	if (daysUntilDue <= INTAKE_DUE_SOON_DAYS) return 'dueSoon';
	return 'none';
}
